import { Injectable, inject } from '@angular/core'
import {
  GetTaskRowsGQL,
  GetTaskRowsPrevPageGQL,
  PropertyType,
  PropertyValue,
  TaskRow,
  TaskRowFilterInput,
  TaskRowSortInput,
  TaskRowsConnection,
} from '@ziptility/graphql'
import { compact, groupBy, keys, without, first } from 'lodash/fp'
import { firstValueFrom } from 'rxjs'
import { map } from 'rxjs/operators'
import { TableData, ColumnRef, TableRow } from '..'

@Injectable()
export class TaskListTableService {
  readonly getTaskRows = inject(GetTaskRowsGQL)
  readonly getTaskRowsPrevPage = inject(GetTaskRowsPrevPageGQL)
  pageSize = 50
  primaryColumnOrder = ['Title', 'TaskTemplate', 'DateCreated', 'Creator', 'DateCompleted', 'Completor']
  selectAssetProperties = []

  async fetch(where: TaskRowFilterInput, order: TaskRowSortInput[]) {
    return firstValueFrom(
      this.getTaskRows
        .fetch(
          {
            where,
            order,
            first: this.pageSize,
            select: [],
            selectAssetProperties: this.selectAssetProperties,
          },
          {
            fetchPolicy: 'network-only',
          },
        )
        .pipe(map((r) => r.data.taskRows as TaskRowsConnection)),
    )
  }

  async nextPage(endCursor: string, where: TaskRowFilterInput, order: TaskRowSortInput[]) {
    return firstValueFrom(
      this.getTaskRows
        .fetch(
          {
            where,
            order,
            first: this.pageSize,
            after: endCursor,
            select: [],
            selectAssetProperties: this.selectAssetProperties,
          },
          {
            fetchPolicy: 'network-only',
          },
        )
        .pipe(map((r) => r.data.taskRows as TaskRowsConnection)),
    )
  }

  async previousPage(startCursor: string, where: TaskRowFilterInput, order: TaskRowSortInput[]) {
    return firstValueFrom(
      this.getTaskRowsPrevPage
        .fetch(
          {
            where,
            order,
            last: this.pageSize,
            before: startCursor,
            select: [],
            selectAssetProperties: this.selectAssetProperties,
          },
          {
            fetchPolicy: 'network-only',
          },
        )
        .pipe(
          map((r) => {
            if (r.error) {
              console.log(r.error)
            }
            if (r.errors) {
              console.log(r.errors)
            }
            return r.data.taskRows as TaskRowsConnection
          }),
        ),
    )
  }

  makeTableRows(taskRows: TaskRow[], taskColumnOrder: string[]): TableData {
    const columns = new Map<string, ColumnRef>()

    const rows = taskRows.map((r): TableRow => {
      const { propertyValues } = r
      const taskPropertyValues = groupBy('name', propertyValues)
      const assetPropertyValues = groupBy('name', r.assetRow?.propertyValues)

      const row: TableRow = { taskId: r.taskId, taskTemplateId: r.taskTemplateId }

      keys(taskPropertyValues).forEach((name) => {
        const propertyValues = taskPropertyValues[name]
        const propertyValue = first(propertyValues)
        columns.set(name, {
          name,
          key: name,
          label: name.replace(/([a-z])([A-Z])/, '$1 $2'),
          visible: true,
          source: 'task',
          category: propertyValue?.category ?? '',
        })
        const type = propertyValue?.type
        if (type) {
          row[name] = {
            propertyKey: name,
            propertyName: name,
            type,
            value: propertyValues.map((pv) => this.getPropertyValue(pv)).join(', '),
            source: 'task',
            format: name.includes('Cost') ? 'currency' : undefined,
          }
        }
      })

      keys(assetPropertyValues).forEach((name) => {
        const uniqueKey = `Asset:${name}`
        const group = assetPropertyValues[name]
        const propertyValue = first(group)
        columns.set(uniqueKey, {
          name,
          key: uniqueKey,
          label: name.replace(/([a-z])([A-Z])/, '$1 $2'),
          visible: true,
          source: 'asset',
          category: propertyValue?.category ?? 'Other',
        })
        const type = propertyValue?.type
        if (type) {
          row[uniqueKey] = {
            propertyKey: uniqueKey,
            propertyName: name,
            type,
            value: group.map((pv) => this.getPropertyValue(pv)).join(', '),
            source: 'asset',
            format: undefined,
          }
        }
      })

      return row
    })

    const names = columns.keys()
    const taskOrder = taskColumnOrder.length ? taskColumnOrder : this.primaryColumnOrder
    const allColumnNames = Array.from(names)
    const order = [...taskOrder, ...without(taskOrder, allColumnNames)]
    return {
      rows,
      columns: compact(order.map((name) => columns.get(name))),
    }
  }

  private getPropertyValue(property: PropertyValue): string | number | boolean | undefined {
    switch (property.type) {
      case PropertyType.StringType:
        return property.stringValue as string | undefined
      case PropertyType.DateTimeType:
        return property.dateTimeValue
      case PropertyType.DecimalType:
        return property.decimalValue
      case PropertyType.IntType:
        return property.intValue as number | undefined
      case PropertyType.BooleanType:
        return property.booleanValue as boolean | undefined
      default:
        return undefined
    }
  }
}
